import cx from 'classnames';
import * as React from 'react';
import { FormGroup, IFormGroupProps } from '../FormGroup/FormGroup';
import { HelpBlock } from '../HelpBlock/HelpBlock';
import { IRadioControlContext, RadioControlContext } from './RadioControlContext';

export interface RadioControlGroupProps<Value extends string | number | boolean | null>
  extends Omit<IFormGroupProps, 'ref'> {
  label?: React.ReactNode;
  description?: React.ReactNode;
  /** error help text for the field that will only be displayed if status is error */
  errorHelpText?: React.ReactNode;
  /**
   * states if the radio input should be arranged vertically
   * @default true
   */
  vertical?: boolean;
  value: Value;
  onChangeValue: (value: Value) => void;
  /** name for this radio group, will be injected into all radio inputs */
  name?: string;
  status?: IRadioControlContext['status'];
  tooltip?: IRadioControlContext['tooltip'];
}

/**
 * `RadioControlGroup` renders containers for a group of radio input and coordinate between them.
 *
 * `RadioControlGroup` and `RadioInput` should be used together as Compound Component.
 * As an metaphor, `RadioControlGroup` is like `select` element while `RadioInput` is like `option` element.
 *
 * `RadioControlGroup` accepts all props `FormGroup` accepts except `status`, where only `'success'` or `'error'` is allowed.
 *
 * If you want to render this wrapper totally differently, instead of add more props to this component, consider create your own wrapper by using `RadioControlContext`.
 */
export const RadioControlGroup = <Value extends string | number | boolean | null>({
  vertical = true,
  required,
  errorHelpText,
  // Context props
  onChangeValue,
  value,
  name,
  status,
  tooltip,
  // children props
  children,
  label,
  description,
  ...formGroupProps
}: RadioControlGroupProps<Value>) => {
  const contextValue = React.useMemo(
    () => ({
      name,
      onChangeValue,
      status,
      tooltip,
      value,
    }),
    [name, onChangeValue, status, tooltip, value]
  );

  return (
    <RadioControlContext.Provider value={contextValue}>
      <fieldset className={cx('tds-RadioGroup tds-RadioGroup--complex', required && 'is-required')}>
        {label && <legend className="tds-RadioGroup-title">{label}</legend>}
        <FormGroup status={status} {...formGroupProps}>
          {description && <p>{description}</p>}
          {errorHelpText && status === 'error' && <HelpBlock>{errorHelpText}</HelpBlock>}
          <div className={cx('input-list', vertical && 'input-list--vertical')}>{children}</div>
        </FormGroup>
        <div />
        {/* Do not remove this line: form-group must not have a bottom margin of 0 when inside a grid */}
      </fieldset>
    </RadioControlContext.Provider>
  );
};
